import { useAllowedLanguages, useUpdateLanguage } from '@map/hooks/locales';
import { makeStyles } from '@map/theme';
import { useSelfNamedInterfaceLocales } from '@shared/hooks/displayNames';
import { useCurrentLocale } from '@shared/hooks/intl';
import Button from '@ui/Button/Button';
import WebsiteIcon from '@ui/Icon/library/WebsiteIcon';
import { ComponentProps } from 'react';
import LanguagePicker from './LanguagePicker';

const useStyles = makeStyles(({ borderRadius, spacing, palette }) => ({
  button: {
    backgroundColor: palette.common.white,
    color: palette.common.black,
    padding: spacing(1.5, 2),
    borderRadius: borderRadius('m'),
    textTransform: 'uppercase',
  },
  icon: {
    color: palette.neutrals[700],
    marginRight: spacing(1),
  },
}));

type Props = Omit<ComponentProps<typeof LanguagePicker>, 'value' | 'onChange'> & {
  forceDropdown?: boolean;
  hideToggleButtonIcon?: boolean;
  toggleButtonClasses?: string;
};

const AppLanguagePicker = (props: Props) => {
  const {
    forceDropdown = false,
    hideToggleButtonIcon = false,
    toggleButtonClasses,
    ...rest
  } = props;
  const currentLocale = useCurrentLocale();
  const { classes, cx } = useStyles();

  const allowedLanguages = useAllowedLanguages();
  const locales = useSelfNamedInterfaceLocales(allowedLanguages);

  const updateLanguage = useUpdateLanguage();

  if (allowedLanguages.length === 1) {
    return null;
  }

  if (allowedLanguages.length === 2 && !forceDropdown) {
    const inactiveLocale = locales.find((locale) => locale.code !== currentLocale);
    return inactiveLocale?.code ? (
      <Button
        size="medium"
        startIcon={!hideToggleButtonIcon ? <WebsiteIcon className={classes.icon} /> : undefined}
        className={cx(classes.button, toggleButtonClasses)}
        onClick={() => updateLanguage(inactiveLocale.code)}
      >
        {inactiveLocale.code}
      </Button>
    ) : null;
  }

  return <LanguagePicker {...rest} value={currentLocale} onChange={updateLanguage} />;
};

export default AppLanguagePicker;
